import { WelcomeMessageButton } from 'models/settings';
import { useRouter } from 'next/router';
import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useBrowserLanguage } from 'hooks/useBrowserLanguage';
import { ReduxState } from 'ducks';
import { selectDigitalMap } from 'ducks/universal/digitalMap';
import { determineChatLanguage } from 'lib/util/chatLanguageDetection';

export interface WelcomeMessage {
  text: string;
  buttons: WelcomeMessageButton[];
}

interface ChatAction {
  welcomeMessage: WelcomeMessage;
  selectedButton: string;
}

interface ChatContextType {
  action: ChatAction | null;
  setAction: (action: ChatAction) => void;
  clearAction: () => void;
  chatLanguage: string;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

interface ChatProviderProps {
  children: ReactNode;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const [action, setActionState] = useState<ChatAction | null>(null);
  const [chatLanguage, setChatLanguage] = useState<string>('en-US');

  const { i18n } = useTranslation();
  const settings = useSelector((state: ReduxState) => state.server.settings.all);
  const digitalMap = useSelector(selectDigitalMap);
  const pageLanguage = i18n.language;
  const browserLanguage = useBrowserLanguage(pageLanguage);

  const setAction = (newAction: ChatAction) => {
    setActionState(newAction);
  };

  const clearAction = () => {
    setActionState(null);
  };

  const router = useRouter();
  const { nav } = router.query;

  // Determine chat language based on settings and browser language
  useEffect(() => {
    const chatLanguageResult = determineChatLanguage(settings?.chat_settings, {
      pathname: router.pathname,
      digitalMapId: digitalMap?.id,
      guestPortalId: router.query.id as string,
      mapPathId: router.query.id as string, // For root map pages, this is the path ID
      digitalMapSettings: settings?.digital_map_settings,
      pageLanguage,
      browserLanguage,
    });

    setChatLanguage(chatLanguageResult.finalLanguage);
  }, [settings, browserLanguage, pageLanguage, router.pathname, router.query.id, digitalMap]);

  React.useEffect(() => {
    // Clear action when navigating away from chat page
    if (nav !== 'svc') {
      setActionState(null);
    }
  }, [nav]);

  return (
    <ChatContext.Provider value={{ action, setAction, clearAction, chatLanguage }}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChatContext = (): ChatContextType | null => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    return null;
  }
  return context;
};
